'use client';

import {useTranslations} from 'next-intl';
import Button from '@/components/ui/Button';
import Card from '@/components/ui/Card';

export default function ContactContent() {
  const t = useTranslations('contact');

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <h1 className="text-4xl font-bold text-primary mb-12">{t('title')}</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Contact Info */}
        <div>
          <Card className="mb-8">
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                  <svg className="w-6 h-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-primary mb-1">{t('label_address')}</h3>
                  <p className="text-muted">{t('address')}</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                  <svg className="w-6 h-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-primary mb-1">{t('label_phone')}</h3>
                  <a href="tel:+21671808066" className="text-secondary hover:text-secondary-light">{t('phone')}</a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                  <svg className="w-6 h-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-primary mb-1">{t('label_email')}</h3>
                  <a href="mailto:utss@planet.tn" className="text-secondary hover:text-secondary-light">{t('email')}</a>
                </div>
              </div>
            </div>
          </Card>

          {/* Google Maps embed — Cite Khadhra, Tunis */}
          <div className="rounded-xl overflow-hidden border border-border h-64">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3194.5!2d10.1345!3d36.8065!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sCit%C3%A9+Khadhra%2C+Tunis!5e0!3m2!1sfr!2stn!4v1"
              width="100%"
              height="100%"
              style={{border: 0}}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Localisation UTSS — Cite Khadhra, Tunis"
            />
          </div>
        </div>

        {/* Contact Form */}
        <Card>
          <form className="space-y-5" onSubmit={(e) => e.preventDefault()}>
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-foreground mb-1">{t('form_name')}</label>
              <input
                id="name"
                type="text"
                placeholder={t('form_name')}
                className="w-full px-4 py-2.5 rounded-lg border border-border bg-white focus:ring-2 focus:ring-secondary/20 focus:border-secondary outline-none transition"
                required
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-foreground mb-1">{t('form_email')}</label>
                <input
                  id="email"
                  type="email"
                  placeholder={t('form_email')}
                  className="w-full px-4 py-2.5 rounded-lg border border-border bg-white focus:ring-2 focus:ring-secondary/20 focus:border-secondary outline-none transition"
                  required
                />
              </div>
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-foreground mb-1">{t('form_phone')}</label>
                <input
                  id="phone"
                  type="tel"
                  placeholder={t('form_phone')}
                  className="w-full px-4 py-2.5 rounded-lg border border-border bg-white focus:ring-2 focus:ring-secondary/20 focus:border-secondary outline-none transition"
                />
              </div>
            </div>
            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-foreground mb-1">{t('form_subject')}</label>
              <select
                id="subject"
                className="w-full px-4 py-2.5 rounded-lg border border-border bg-white focus:ring-2 focus:ring-secondary/20 focus:border-secondary outline-none transition"
                required
                defaultValue=""
              >
                <option value="" disabled>{t('choose_subject')}</option>
                <option value="general">{t('subject_general')}</option>
                <option value="benevolat">{t('subject_volunteering')}</option>
                <option value="partenariat">{t('subject_partnership')}</option>
                <option value="presse">{t('subject_press')}</option>
                <option value="reclamation">{t('subject_complaint')}</option>
              </select>
            </div>
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-foreground mb-1">{t('form_message')}</label>
              <textarea
                id="message"
                rows={5}
                placeholder={t('form_message')}
                className="w-full px-4 py-2.5 rounded-lg border border-border bg-white focus:ring-2 focus:ring-secondary/20 focus:border-secondary outline-none transition resize-y"
                required
              />
            </div>
            <Button type="submit" variant="primary" size="lg" className="w-full">
              {t('form_submit')}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
